import { Dispatch, useContext, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import OARangePicker from '../../../components/OARangePicker';
import { KTCard, KTCardBody } from '../../../_metronic/helpers';
import LedgerContext, { ILedgerContext } from '../context/LedgerContext';
import { ILedgerQueryParams } from '../type';
import OAModalComponent from '../../../components/OAModalComponent';
import OAButton from '../../../components/widgets/OAButton';
import AddTransactionForm from '../Create/AddTransactionForm';
import AddTopupSettlementForm from '../Create/AddTopupSettlementForm';
import UploadSettlementCsvModal from '../Create/UploadSettlementCsvModal';
import { useAuth } from '../../../auth';
import OATableComponent, { OATablePaginationProps } from '../../../components/OATableComponent';
import { getLedgerColumns, verticalLedgerColumns } from './LedgerColumns';

const customSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    fontSize: '0.925rem',
    minHeight: '40px',
    height: '40px',
    borderRadius: '0.475rem',
    borderColor: state.isFocused ? '#009EF7' : '#E4E6EF',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0, 158, 247, 0.25)' : 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.6 : 1,
    '&:hover': {
      borderColor: state.isFocused ? '#009EF7' : '#E4E6EF'
    }
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: '0 12px',
    height: '38px'
  }),
  input: (base: any) => ({
    ...base,
    margin: '0',
    padding: '0'
  }),
  indicatorsContainer: (base: any) => ({
    ...base,
    height: '38px'
  }),
  loadingIndicator: (base: any) => ({
    ...base,
    color: '#009EF7'
  }),
  menu: (base: any) => ({
    ...base,
    fontSize: '0.925rem',
    zIndex: 9999
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: '0.925rem',
    backgroundColor: state.isSelected ? '#009EF7' : state.isFocused ? '#F5F8FA' : 'white',
    color: state.isSelected ? 'white' : '#181C32',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: state.isSelected ? '#009EF7' : '#F5F8FA'
    }
  }),
  loadingMessage: (base: any) => ({
    ...base,
    fontSize: '0.925rem',
    color: '#A1A5B7'
  })
};

interface ITransactionHistory {
  queryParams: ILedgerQueryParams;
  setQueryParams: Dispatch<React.SetStateAction<ILedgerQueryParams>>;
  onChangeRangePicker: (range: any) => void;
}

const TransactionHistory = (props: ITransactionHistory) => {
  const { queryParams, setQueryParams, onChangeRangePicker } = props;
  const { downloadReport, downloadInternalLedgerReport, ledgerList, internalLedgerList, supplierTransactionsList, getLedgerInfo, pageInfo,getSupplierTransactions }: ILedgerContext =
    useContext(LedgerContext);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isTransactionModalOpen, setTransactionModalOpen] = useState<boolean>(false);
  const [isTopupSettlementModalOpen, setTopupSettlementModalOpen] = useState<boolean>(false);
  const [isUploadCsvModalOpen, setUploadCsvModalOpen] = useState<boolean>(false);
  const [transactionData, setTransactionData] = useState<any>(null);
  const [ledgerInfo, setLedgerInfo] = useState<any>(null);
  const [isCustomerLoading, setIsCustomerLoading] = useState<boolean>(false);
  const { currentUser } = useAuth();
  const isOnArrivalOrg = internalLedgerList?.ledgerType !== 'ONARRIVAL'
  const isSupplierView = internalLedgerList?.supplierId

  useEffect(()=>{
    if(isSupplierView){
      getSupplierTransactions()
    }
  }, [isSupplierView])
  // Determine entry type options based on organization type  
  const toggleTransactionModal = () => {
    if (isTransactionModalOpen) {
      setTransactionData(null);
    }
    setTransactionModalOpen(prev => !prev);
  };

  const toggleTopupSettlementModal = () => {
    setTopupSettlementModalOpen(prev => !prev);
  };

  const toggleUploadCsvModal = () => {
    setUploadCsvModalOpen(prev => !prev);
  };

  const refreshTransactionList = () => {
    if (queryParams?.customerId) {
      getSupplierTransactions();
    }
  };

  const download = () => {
    setIsGenerating(true);

    // Use internal ledger download if data is from supplier transactions or internal ledger, otherwise use regular download
    const downloadFunc = hasSupplierTransactions || hasInternalLedger ? downloadInternalLedgerReport : downloadReport;

    downloadFunc(() => {
      setIsGenerating(false);
    });
  };

  useEffect(() => {
    getLedgerInfo(res => {
      setLedgerInfo(res);
    });
  }, []);

  const handleEditTransaction = (data: any) => {
    toggleTransactionModal();
    setTransactionData(data);
  };

  // Check if supplier transactions response exists (move this up before customerOptions)
  const hasSupplierTransactions = supplierTransactionsList?.supplierId !== undefined;

  // Create customer options from customerBreakdown with Onarrival as first option
  // Only show for supplier organizations
  const customerOptions = useMemo(() => {
    // Only show customer dropdown for supplier view
    if (!isSupplierView) {
      return [];
    }

    const options = internalLedgerList?.customerBreakdown?.map((customer: any) => ({
      value: customer.customerId,
      label: `${customer.customerName}`
    })) ?? [];

    // Add Onarrival as the first option for supplier view
    if (hasSupplierTransactions) {
      return [{ value: 'ONARRIVAL', label: 'Onarrival' }, ...options];
    }

    return options;
  }, [internalLedgerList?.customerBreakdown, hasSupplierTransactions, isSupplierView]);

  // Handle customer selection
  const handleCustomerChange = (selectedOption: any) => {
    setIsCustomerLoading(true);
    setQueryParams(prev => ({
      ...prev,
      customerId: selectedOption?.value,
      pageIndex: 1 // Reset to first page when changing customer
    }));
  };

  // Set default to Onarrival for supplier view (first option), otherwise first customer
  // Only for supplier organizations
  useEffect(() => {
    if (isSupplierView && customerOptions.length > 0 && !queryParams?.customerId) {
      setQueryParams(prev => ({
        ...prev,
        customerId: 'ONARRIVAL'
      }));
    }
  }, [customerOptions, isSupplierView]);

  // Turn off loading when supplier transactions are fetched
  useEffect(() => {
    if (supplierTransactionsList) {
      setIsCustomerLoading(false);
    }
  }, [supplierTransactionsList]);

  // Determine which list to use - priority: supplierTransactionsList > internalLedgerList > ledgerList
  const hasInternalLedger = internalLedgerList?.content?.length > 0;
  const activeList = hasSupplierTransactions
    ? supplierTransactionsList.content
    : hasInternalLedger
      ? internalLedgerList.content
      : ledgerList;

  const tableColumns = useMemo(() => {
    // Use vertical columns for supplier transactions and internal ledger
    return hasSupplierTransactions || hasInternalLedger
      ? verticalLedgerColumns(handleEditTransaction)
      : getLedgerColumns(handleEditTransaction);
  }, [hasSupplierTransactions, hasInternalLedger]);

  // Use pagination from active list - prioritize supplier transactions, then internal ledger, then pageInfo
  let activePaginationSource;
  if (hasSupplierTransactions) {
    activePaginationSource = supplierTransactionsList;
  } else if (hasInternalLedger) {
    activePaginationSource = internalLedgerList;
  } else {
    activePaginationSource = pageInfo;
  }

  const paginationProps: OATablePaginationProps = {
    pageIndex: queryParams?.pageIndex,
    pageCount: activePaginationSource?.totalPages,
    total: activePaginationSource?.totalElements ?? activePaginationSource?.totalCount,
    pageSize: activePaginationSource?.size,
    onPageChange: (value: any) => {
      setQueryParams(prev => ({ ...prev, pageIndex: value }));
    }
  };

  return (
    <div>
      <KTCard>
        <div className='card-header min-h-50px align-items-center px-6 py-4'>
          <h3 className='m-0'>Transaction history</h3>
          <div className='d-flex align-items-end'>
            <OARangePicker
              name='ledger-data-range'
              className='mw-250px'
              onChangeRangePicker={onChangeRangePicker}
              dateRange={queryParams?.dateRange}
              maxDate={new Date()}
            />

            {customerOptions.length > 0 && (
              <div className='ms-2 mw-250px'>
                <Select
                  options={customerOptions}
                  onChange={handleCustomerChange}
                  value={customerOptions.find((opt: any) => opt.value === queryParams?.customerId) || null}
                  placeholder='Select Customer'
                  isClearable={false}
                  isLoading={isCustomerLoading}
                  loadingMessage={() => 'Loading transactions...'}
                  styles={customSelectStyles}
                  className='customer-select'
                  classNamePrefix='oa-select'
                />
              </div>
            )}
            <OAButton
              onClick={download}
              className='btn btn-sm btn-flex btn-light-primary fw-bolder text-nowrap ms-2'
              disabled={isGenerating || (activeList && activeList.length == 0)}
              icon='/media/icons/duotune/files/fil021.svg'
              iconClassName='svg-icon-3 me-2'
              isLoading={isGenerating}
              loadingText='Generating...'
              label='Download CSV'
            />

            {currentUser?.data?.role === 'ROLE_SUPER_ADMIN' && ledgerInfo?.active && !hasInternalLedger && !hasSupplierTransactions && (
              <OAButton
                onClick={() => setTransactionModalOpen(true)}
                label='Add Transaction'
                className='ms-2'
              />
            )}
            {
              isOnArrivalOrg && !isSupplierView &&<OAButton
                onClick={() => setTopupSettlementModalOpen(true)}
                label='Add Topup Settlement'
                className='ms-2'
              />
            }
            {
              isOnArrivalOrg && !isSupplierView &&<OAButton
                onClick={() => setUploadCsvModalOpen(true)}
                label='Upload Settlements CSV'
                className='ms-2'
                icon='/media/icons/duotune/files/fil018.svg'
                iconClassName='svg-icon-3 me-2'
              />
            }
          </div>
        </div>
        <KTCardBody className='px-6 py-5'>
          <OATableComponent
            columns={tableColumns}
            data={activeList ?? []}
            pagination={paginationProps}
            loading={isCustomerLoading}
            disableColumnMenu={false}
            getRowId={(row) => row.entryId || row.id}
          />
        </KTCardBody>
      </KTCard>
      <OAModalComponent
        className='transactional-modal'
        show={isTransactionModalOpen}
        onHide={toggleTransactionModal}
        header={{
          title: `${transactionData ? 'Edit' : 'Add'} Transaction`,
          closeButton: true
        }}
        body={{
          content: <AddTransactionForm onHide={toggleTransactionModal} data={transactionData} />
        }}
      />
      <OAModalComponent
        className='topup-settlement-modal'
        show={isTopupSettlementModalOpen}
        onHide={toggleTopupSettlementModal}
        header={{
          title: 'Add Topup Settlement',
          closeButton: true
        }}
        body={{
          content: <AddTopupSettlementForm onHide={toggleTopupSettlementModal} onSuccess={refreshTransactionList} />
        }}
      />
      <OAModalComponent
        className='upload-csv-modal'
        show={isUploadCsvModalOpen}
        onHide={toggleUploadCsvModal}
        header={{
          title: 'Upload Settlements CSV',
          closeButton: true
        }}
        body={{
          content: <UploadSettlementCsvModal onHide={toggleUploadCsvModal} />
        }}
      />
    </div>
  );
};

export default TransactionHistory;